import { useEffect, useState, type ReactNode } from "react";
import { ErrorBanner } from "@/components/board/ErrorBanner";
import { LoadingState } from "@/components/board/LoadingState";
import { useToast } from "@/components/Toast";
import { useUserSettings, type UserSettings as UserSettingsValues } from "@/contexts/UserSettingsContext";
import { isAvailabilityMode } from "@/utils/makersUtils";

interface SettingRowProps {
  label: string;
  description?: string;
  htmlFor?: string;
  children: ReactNode;
}

function SettingRow({ label, description, htmlFor, children }: SettingRowProps) {
  return (
    <div className="user-settings__row">
      <div className="user-settings__row-text">
        <label className="user-settings__row-label" htmlFor={htmlFor}>
          {label}
        </label>
        {description && <p className="user-settings__row-description">{description}</p>}
      </div>
      <div className="user-settings__row-control">{children}</div>
    </div>
  );
}

export function UserSettings() {
  const { settings, loading, error, update } = useUserSettings();
  const { addToast } = useToast();

  // Local string for the Min hours input so typing stays smooth; the parsed
  // value is committed (saved) on blur. Kept in sync when the saved value changes.
  const [minHoursInput, setMinHoursInput] = useState(String(settings.availabilityMinHours));
  useEffect(() => {
    setMinHoursInput(String(settings.availabilityMinHours));
  }, [settings.availabilityMinHours]);

  const save = async (patch: Partial<UserSettingsValues>) => {
    const ok = await update(patch);
    if (!ok) addToast("Failed to save setting. Please retry.", "error");
  };

  const commitMinHours = () => {
    const trimmed = minHoursInput.trim();
    // The DB column is an integer, so only accept a plain non-negative integer.
    // This rejects values that parseInt would silently truncate ("1.5", "1e2")
    // as well as negatives and empty input — reverting the field in those cases.
    if (!/^\d+$/.test(trimmed)) {
      setMinHoursInput(String(settings.availabilityMinHours));
      return;
    }
    const parsed = parseInt(trimmed, 10);
    if (parsed !== settings.availabilityMinHours) {
      save({ availabilityMinHours: parsed });
    } else {
      // Normalize the display (e.g. "040" -> "40") when the value is unchanged.
      setMinHoursInput(String(parsed));
    }
  };

  if (loading) {
    return <LoadingState message="Loading settings…" />;
  }

  return (
    <div className="app-settings">
      {error && <ErrorBanner message={error} entityName="settings" />}

      <section className="settings-section">
        <div className="settings-section__header">
          <h2 className="settings-section__title">Availability</h2>
        </div>
        <div className="user-settings__rows">
          <SettingRow
            label="Availability mode"
            description="Default for the availability toggle on the Availability and Makers boards: count all work, confirmed-only, or sold (Won) work only."
            htmlFor="user-setting-availability-mode"
          >
            <select
              id="user-setting-availability-mode"
              className="user-settings__select"
              value={settings.availabilityMode}
              onChange={(e) => {
                if (isAvailabilityMode(e.target.value)) {
                  save({ availabilityMode: e.target.value });
                }
              }}
            >
              <option value="all">Include unconfirmed</option>
              <option value="confirmed">Confirmed only</option>
              <option value="sold">Sold work only</option>
            </select>
          </SettingRow>

          <SettingRow
            label="Minimum hours"
            description="Default minimum available hours used to filter the Availability board."
            htmlFor="user-setting-min-hours"
          >
            <input
              id="user-setting-min-hours"
              type="number"
              className="user-settings__number"
              min={0}
              step={1}
              value={minHoursInput}
              onChange={(e) => setMinHoursInput(e.target.value)}
              onBlur={commitMinHours}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
              }}
            />
          </SettingRow>
        </div>
      </section>

      <section className="settings-section">
        <div className="settings-section__header">
          <h2 className="settings-section__title">Projects</h2>
        </div>
        <div className="user-settings__rows">
          <SettingRow
            label="Default expand/collapse"
            description="How the Projects board loads each session."
            htmlFor="user-setting-expand-default"
          >
            <select
              id="user-setting-expand-default"
              className="user-settings__select"
              value={settings.projectsExpandedByDefault ? "expanded" : "collapsed"}
              onChange={(e) => save({ projectsExpandedByDefault: e.target.value === "expanded" })}
            >
              <option value="expanded">All expanded</option>
              <option value="collapsed">All collapsed</option>
            </select>
          </SettingRow>

          <SettingRow
            label="Preserve status"
            description="When on, your expanded/collapsed projects are remembered across reloads for this session. When off, the board always reverts to the default above on load."
            htmlFor="user-setting-preserve"
          >
            <input
              id="user-setting-preserve"
              type="checkbox"
              className="user-settings__checkbox"
              checked={settings.preserveExpandCollapse}
              onChange={(e) => save({ preserveExpandCollapse: e.target.checked })}
            />
          </SettingRow>
        </div>
      </section>
    </div>
  );
}
